import React, { useState, useEffect } from "react";
import { TextField, Button } from "@mui/material";
import { PMDialog } from "../../../utils/PMDialog";
import { contractLinesApi } from "../../../../api/contract_lines";
import { axiosWithToast } from "../../../Utils";

export const DeliveryProgressDialog = ({
  open,
  onClose,
  onSuccess,
  project,
  contractLine,
}) => {
  const [loading, setLoading] = useState(false);
  const [newQuantity, setNewQuantity] = useState("");
  const [totalAfterUpdate, setTotalAfterUpdate] = useState(0);

  useEffect(() => {
    if (open && contractLine) {
      setNewQuantity("");
      calculateTotal();
    }
  }, [open, contractLine]);

  const calculateTotal = () => {
    if (!contractLine) return;
    const current = parseFloat(contractLine.completed_quantity) || 0;
    const newQty = parseFloat(newQuantity) || 0;
    setTotalAfterUpdate(current + newQty);
  };

  useEffect(() => {
    calculateTotal();
  }, [newQuantity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project?.id || !contractLine) return;

    const newCompletedQuantity = parseFloat(newQuantity) || 0;
    const currentCompletedQuantity = parseFloat(contractLine.completed_quantity) || 0;
    const totalCompletedQuantity = currentCompletedQuantity + newCompletedQuantity;

    // Validate
    if (newCompletedQuantity <= 0) {
      alert("Ny utført mengde må være større enn 0");
      return;
    }

    if (
      contractLine.contract_quantity &&
      totalCompletedQuantity > contractLine.contract_quantity
    ) {
      alert(
        `Total utført mengde (${totalCompletedQuantity}) kan ikke overstige kontraktsmengde (${contractLine.contract_quantity})`
      );
      return;
    }

    setLoading(true);

    try {
      await axiosWithToast(
        contractLinesApi.registerProgress(
          project.id,
          contractLine.id,
          totalCompletedQuantity
        ),
        () => {
          onSuccess();
        },
        () => {},
        () => {},
        "Registrerer fremdrift..."
      );
    } catch (error) {
      console.error("Error registering progress:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!contractLine) return null;

  const formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
  });

  const currentCompleted = parseFloat(contractLine.completed_quantity) || 0;
  const contractQty = parseFloat(contractLine.contract_quantity) || 0;
  const unitTitle = contractLine.unit?.title || "";

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="Registrer utført mengde"
      size="lg"
      closeOnOutsideClick={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Linje: {contractLine.product?.displayTitle || contractLine.title || contractLine.description || "Ingen tittel"}
          </div>
          <div className="text-sm text-gray-600">
            Kontraktsmengde: {contractQty} {unitTitle}
          </div>
          <div className="text-sm text-gray-600">
            Allerede utført: {currentCompleted} {unitTitle}
          </div>
        </div>

        <TextField
          label={`Ny utført mengde (${unitTitle})`}
          type="number"
          value={newQuantity}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
              setNewQuantity(value);
            }
          }}
          fullWidth
          variant="standard"
          required
          inputProps={{ step: "0.01", min: "0" }}
          helperText={`Total utført etter registrering: ${currentCompleted} + ${newQuantity || 0} = ${totalAfterUpdate} ${unitTitle}`}
        />

        {contractLine.unit_price && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm font-medium text-blue-900">
              Fakturerbart beløp etter registrering:
            </div>
            <div className="text-lg font-semibold text-blue-900">
              {formatter.format((totalAfterUpdate || 0) * (contractLine.unit_price || 0))}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
          <Button onClick={onClose} disabled={loading}>
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !newQuantity || parseFloat(newQuantity) <= 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? "Registrerer..." : "Registrer"}
          </Button>
        </div>
      </form>
    </PMDialog>
  );
};

